import { useEffect, useState } from "react";
import {
  Loader2,
  Send,
  Trash2,
  WifiOff,
  RefreshCw,
  Inbox,
  Camera,
  PenLine,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { getPendingOperations } from "@/lib/offlineDb";
import {
  flushQueue,
  syncOne,
  discardOne,
  subscribeQueue,
} from "@/lib/syncQueue";

const typeLabel = {
  DEPOT: "Dépôt",
  RESTITUTION: "Restitution",
  DELIVRANCE: "Délivrance",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Queue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);
  const [busyAll, setBusyAll] = useState(false);
  const [busyById, setBusyById] = useState({});

  const load = async () => {
    try {
      const data = await getPendingOperations();
      setItems(data);
    } catch (err) {
      // IndexedDB may be unavailable (private mode) — keep the empty list
      // and log so the issue is debuggable without crashing the UI.
      console.warn("[Queue] getPendingOperations failed", err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const unsubscribe = subscribeQueue(() => load());
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      unsubscribe();
    };
    // `load`, `subscribeQueue` and the setters are stable in this scope —
    // this effect runs once on mount to wire up the subscriptions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFlushAll = async () => {
    if (!online || busyAll) return;
    setBusyAll(true);
    try {
      const sent = await flushQueue();
      if (sent === 0) {
        toast.info("Aucune opération synchronisée", {
          description: "Le serveur n'est peut-être pas joignable.",
        });
      }
    } finally {
      setBusyAll(false);
    }
  };

  const handleSyncOne = async (id) => {
    setBusyById((b) => ({ ...b, [id]: "sync" }));
    try {
      const ok = await syncOne(id);
      if (ok) {
        toast.success("Bordereau envoyé");
      } else {
        toast.error("Envoi impossible", {
          description: online ? "Vérifiez le serveur." : "Pas de connexion.",
        });
      }
    } finally {
      setBusyById((b) => {
        const { [id]: _, ...rest } = b;
        return rest;
      });
    }
  };

  const handleDiscard = async (id, name) => {
    if (
      !window.confirm(
        `Supprimer le bordereau en attente pour ${name || "ce client"} ? Cette action est irréversible.`,
      )
    )
      return;
    setBusyById((b) => ({ ...b, [id]: "discard" }));
    try {
      await discardOne(id);
      toast.success("Bordereau supprimé de la file");
    } finally {
      setBusyById((b) => {
        const { [id]: _, ...rest } = b;
        return rest;
      });
    }
  };

  return (
    <div data-testid="queue-page">
      <PageHeader
        title="File d'attente"
        subtitle={`${items.length} bordereau${items.length > 1 ? "x" : ""} en attente d'envoi`}
        backTo="/profil"
        right={
          items.length > 0 && (
            <button
              data-testid="flush-all-btn"
              onClick={handleFlushAll}
              disabled={!online || busyAll}
              className="px-3 py-2 rounded-xl bg-[var(--vidoc-primary)] text-white text-sm font-medium flex items-center gap-1.5 touch-target disabled:opacity-50"
              title={!online ? "Connectez-vous pour synchroniser" : "Tout envoyer"}
            >
              {busyAll ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Tout synchroniser
            </button>
          )
        }
      />

      <div className="px-4 md:px-8 py-6 max-w-3xl space-y-4">
        {!online && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-start gap-3">
            <WifiOff className="w-5 h-5 text-[var(--vidoc-warning)] mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-semibold text-orange-900">Mode hors ligne</div>
              <div className="text-[var(--vidoc-text-secondary)]">
                Les bordereaux ci-dessous seront envoyés automatiquement dès le retour de la connexion.
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-[var(--vidoc-text-secondary)]">
            Chargement...
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-[var(--vidoc-border)] p-12 text-center">
            <Inbox className="w-12 h-12 mx-auto text-[var(--vidoc-text-tertiary)] mb-3" />
            <div className="font-medium text-[var(--vidoc-text)]">
              Aucun bordereau en attente
            </div>
            <div className="text-sm text-[var(--vidoc-text-secondary)] mt-1">
              Tous vos bordereaux ont été envoyés au serveur.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => {
              const name =
                [item.client_snapshot?.nom, item.client_snapshot?.prenom]
                  .filter(Boolean)
                  .join(" ") || "Client inconnu";
              const photos = (item.body?.medias || []).filter(
                (m) => m.type === "PHOTO",
              ).length;
              const hasSig = (item.body?.medias || []).some(
                (m) => m.type === "SIGNATURE",
              );
              return (
                <div
                  key={item.id}
                  data-testid={`queue-item-${item.id}`}
                  className="bg-white rounded-xl border border-[var(--vidoc-border)] p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{name}</div>
                      <div className="text-xs text-[var(--vidoc-text-secondary)]">
                        {typeLabel[item.type] || item.type} ·{" "}
                        {fmtDate(item.queued_at)}
                      </div>
                      <div className="mt-2 flex items-center gap-3 text-xs text-[var(--vidoc-text-secondary)]">
                        <span className="inline-flex items-center gap-1">
                          <Camera className="w-3.5 h-3.5" />
                          {photos} photo{photos > 1 ? "s" : ""}
                        </span>
                        {hasSig && (
                          <span className="inline-flex items-center gap-1 text-[var(--vidoc-success)]">
                            <PenLine className="w-3.5 h-3.5" />
                            Signature
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      data-testid={`queue-sync-${item.id}`}
                      onClick={() => handleSyncOne(item.id)}
                      disabled={!online || busyById[item.id]}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[var(--vidoc-primary)] text-white text-sm font-medium touch-target disabled:opacity-50"
                    >
                      {busyById[item.id] === "sync" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Envoyer maintenant
                    </button>
                    <button
                      data-testid={`queue-discard-${item.id}`}
                      onClick={() => handleDiscard(item.id, name)}
                      disabled={busyById[item.id]}
                      className="px-3 py-2 rounded-lg bg-red-50 text-[var(--vidoc-danger)] hover:bg-red-100 touch-target disabled:opacity-50"
                      title="Supprimer de la file"
                    >
                      {busyById[item.id] === "discard" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
